



































































































































































import VueElementLoading from "vue-element-loading"
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import moment from "moment"
import { cloneDeep } from "lodash"
import { Classificator } from "../../../common/types/Classificator"
import { Indicator } from "../../../common/types/Indicator"
import { Segmentation } from "../../../common/types/roots/fields"
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import { ReportDkiRRow } from "../table-data/ReportDkiRRow"

/** Фиксированный порядок разрезов расходов внутри уровня «КБК» (RQ.eAk.RcDkiR.25) */
const KBK_ORDER = ['gr', 'pgr', 'abp', 'prg', 'ppr', 'spf']

/**
 * Начальная оценка высоты строки тела таблицы (px) — используется для окна виртуализации
 * (RQ.eAk.RcDkiR.54), пока не измерена реальная высота отрисованных строк (rowHeight,
 * измеряется в mounted()/onFilteredRowsChanged() по первой отрисованной строке, по образцу
 * report-svod-plan.vue).
 */
const INITIAL_ROW_HEIGHT = 34
/** Запас строк сверху/снизу видимого окна, чтобы избежать "мигания" при скролле */
const ROW_BUFFER = 15

/** Ширина (px) sticky-колонки с кнопкой раскрытия/сворачивания дерева (RQ.eAk.RcDkiR.55) */
const TOGGLE_COLUMN_WIDTH = 40

/** Ширины (px) колонок-боковика для расчёта sticky-отступов (RQ.eAk.RcDkiR.55) */
const COLUMN_WIDTHS: Record<string, number> = {
    region: 70,
    gr: 50,
    pgr: 50,
    abp: 50,
    gu: 70,
    prg: 50,
    ppr: 50,
    spf: 50,
    name: 260
}

@Component({
    components: {
        'loading': VueElementLoading
    }
})
export default class ReportDkiR extends Vue {

    @Prop({ type: Array, required: true, default: () => [] })
    public readonly tableData!: ReportDkiRRow[]

    @Prop({ type: Array, required: true, default: () => [] })
    private readonly propIndicators!: Indicator[]

    @Prop({ type: Array, required: true, default: () => [] })
    private readonly propMonths!: Indicator[]

    @Prop({ type: Array, required: true, default: () => [] })
    private readonly expenses!: Classificator[]

    @Prop({ type: Array, default: () => [] })
    private readonly segmentations!: Array<{ value: Segmentation; active: boolean }>

    @Prop({ type: Boolean, required: true, default: true })
    private readonly isTree!: boolean

    @Prop({ type: Date, default: () => new Date() })
    private readonly date!: Date

    public indicators: Indicator[] = []
    public months: Indicator[] = []

    /**
     * Состояние кнопки «раскрыть/свернуть всё» в шапке (аналог CASH-EXEC).
     * По умолчанию true: строки приходят с бэкенда уже свёрнутыми (row.collapse=true
     * для каждого узла, RQ.eAk.RcDkiR.29) — кнопка должна отражать это состояние.
     */
    public tableCollapse = true

    public scrollTop = 0
    public containerHeight = 800

    private created() {
        this.indicators = cloneDeep(this.propIndicators)
        this.months = cloneDeep(this.propMonths)
        this.rebuildTreeIndex()
    }

    private mounted() {
        this.$nextTick(() => {
            const container = this.$refs.tableContainer as HTMLElement | undefined
            if (container) {
                const firstRow = container.querySelector('tbody tr[data-row-index]') as HTMLElement | null
                if (firstRow && firstRow.offsetHeight > 0) {
                    this.rowHeight = firstRow.offsetHeight
                }
            }
            this.initRowHeights(this.filteredRows.length)
            this.syncVirtualWindow()
        })
        // Capture-phase listener catches scroll on ANY element in the document tree —
        // работает независимо от того, скроллится ли сам tableContainer, родительский
        // .main-container или window (RQ.eAk.RcDkiR.54, по образцу report-svod-plan.vue).
        window.addEventListener('scroll', this.onScroll, { passive: true, capture: true })
        window.addEventListener('resize', this.syncVirtualWindow)
    }

    private beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll, true)
        window.removeEventListener('resize', this.syncVirtualWindow)
        if (this._scrollRaf !== null) {
            cancelAnimationFrame(this._scrollRaf)
        }
        if (this._scrollPointerTimer !== null) {
            clearTimeout(this._scrollPointerTimer)
        }
    }

    // ------------------------------------------------------------------
    // Разрезы / боковик (RQ.eAk.RcDkiR.34, RQ.eAk.RcDkiR.17)
    // ------------------------------------------------------------------

    public get activeExpenseKeys(): string[] {
        return this.expenses.filter(it => it.active).map(it => it.key)
    }

    /**
     * Состав и порядок колонок-боковика. Разрезность задаёт порядок уровней иерархии;
     * внутри "КБК" порядок фиксирован (ФГ→ФПГ→АБП→БП→БПП→СПФ), только активные разрезы.
     * Если разрезность "ГУ" стоит сразу после "КБК", колонка ГУ вставляется сразу после
     * АБП, если АБП активен (RQ.eAk.RcDkiR.17); если АБП не выбран — после всего блока
     * "КБК"; в остальных случаях ГУ — на своей позиции в разрезности.
     */
    public get sidebarColumns(): string[] {
        const segs = (this.segmentations ?? []).filter(it => it.active).map(it => it.value)
        const activeExpenses = this.activeExpenseKeys
        const columns: string[] = []

        for (let i = 0; i < segs.length; i++) {
            const seg = segs[i]
            if (seg === 'REGION') {
                columns.push('region')
            } else if (seg === 'GU') {
                if (!columns.includes('gu')) {
                    columns.push('gu')
                }
            } else if (seg === 'KBK') {
                const guRightAfter = segs[i + 1] === 'GU'
                let guInserted = false
                for (const code of KBK_ORDER) {
                    if (activeExpenses.includes(code)) {
                        columns.push(code)
                    }
                    if (guRightAfter && code === 'abp' && activeExpenses.includes('abp')) {
                        columns.push('gu')
                        guInserted = true
                    }
                }
                if (guRightAfter && !guInserted) {
                    columns.push('gu')
                }
                if (guRightAfter) {
                    i++ // "GU" уже размещён внутри блока КБК — не обрабатывать его отдельно
                }
            }
        }
        columns.push('name')
        return columns
    }

    public sidebarCellStyle(column: string, isHeader: boolean, row: ReportDkiRRow | null = null): Record<string, string> {
        const columns = this.sidebarColumns
        const index = columns.indexOf(column)
        let left = this.isTree ? TOGGLE_COLUMN_WIDTH : 0
        for (let i = 0; i < index; i++) {
            left += COLUMN_WIDTHS[columns[i]] ?? 90
        }
        const width = COLUMN_WIDTHS[column] ?? 90
        const style: Record<string, string> = {
            position: 'sticky',
            left: left + 'px',
            width: width + 'px',
            minWidth: width + 'px',
            maxWidth: width + 'px',
            zIndex: isHeader ? '5' : '2',
            backgroundColor: row && row.rowLevel === 'total' ? '#EEF1F8' : '#fff'
        }
        if (isHeader) {
            style.backgroundColor = '#F7F9FC'
        }
        return style
    }

    /**
     * Стиль sticky-колонки с кнопкой раскрытия/сворачивания дерева (крайняя левая, before
     * sidebarColumns). Раньше не была sticky в отличие от остальных колонок-боковика —
     * при горизонтальном скролле уезжала за пределы видимости, а колонки-боковик оставались
     * зафиксированы, из-за чего кнопки раскрытия дерева пропадали.
     */
    public toggleCellStyle(isHeader: boolean, row: ReportDkiRRow | null = null): Record<string, string> {
        const style: Record<string, string> = {
            position: 'sticky',
            left: '0',
            width: TOGGLE_COLUMN_WIDTH + 'px',
            minWidth: TOGGLE_COLUMN_WIDTH + 'px',
            maxWidth: TOGGLE_COLUMN_WIDTH + 'px',
            zIndex: isHeader ? '5' : '2',
            backgroundColor: row && row.rowLevel === 'total' ? '#EEF1F8' : '#fff'
        }
        if (isHeader) {
            style.backgroundColor = '#F7F9FC'
        }
        return style
    }

    public rowIndentLevel(row: ReportDkiRRow): number {
        // Отступ по уровню вложенности определяется порядком заполненных кодов боковика
        // (RQ.eAk.RcDkiR.29): чем больше кодов классификации заполнено — тем глубже уровень.
        let level = 0
        for (const column of this.sidebarColumns) {
            if (column === 'name') continue
            if (row[column as keyof ReportDkiRRow]) {
                level++
            }
        }
        return level
    }

    // ------------------------------------------------------------------
    // Показатели / Месяцы (RQ.eAk.RcDkiR.19, RQ.eAk.RcDkiR.20)
    // ------------------------------------------------------------------

    public get activeIndicators(): Indicator[] {
        return this.indicators.filter(it => it.active)
    }

    public get activeMonths(): Indicator[] {
        return this.months.filter(it => it.active)
    }

    public isLastActiveIndicator(indicator: Indicator): boolean {
        return indicator.active && this.activeIndicators.length <= 1
    }

    public onIndicatorsChange() {
        this.$emit('update-indicators', cloneDeep(this.indicators))
        this.$nextTick(() => this.syncVirtualWindow())
    }

    public onMonthsChange() {
        this.$emit('update-months', cloneDeep(this.months))
        this.$nextTick(() => this.syncVirtualWindow())
    }

    public monthValue(row: ReportDkiRRow, month: number, indicatorKey: string): number | null {
        const monthData = row.months?.find(it => it.month === month)
        if (!monthData) {
            return null
        }
        return (monthData as any)[indicatorKey] ?? null
    }

    public get totalColumnsCount(): number {
        return (this.isTree ? 1 : 0) + this.sidebarColumns.length + this.activeIndicators.length + (this.activeMonths.length * this.activeIndicators.length)
    }

    // ------------------------------------------------------------------
    // Дерево (RQ.eAk.RcDkiR.26, RQ.eAk.RcDkiR.27, RQ.eAk.RcDkiR.29)
    // ------------------------------------------------------------------

    public get filteredRows(): ReportDkiRRow[] {
        if (this.isTree) {
            return this.tableData.filter(it => it.visible)
        }
        // В плоском режиме выводятся только листовые строки (без дочерних) — RQ.eAk.RcDkiR.27.
        // Строка "ИТОГО" (без родителя, но и без детей на верхнем уровне при плоском выводе тоже
        // не является листом с кодами) выводится первой всегда — RQ.eAk.RcDkiR.28/31.
        return this.tableData.filter(it => it.rowLevel === 'total' || !it.hasChild)
    }

    /**
     * Индекс parent_idx -> дети и idx -> строка. Раньше collapse() при каждом обращении
     * пересканировал весь tableData (`.find()` на O(n) + скан всех строк на O(n)) на
     * КАЖДОМ уровне рекурсии — на дереве в тысячи строк это фактически O(n^2) и хуже,
     * отсюда подвисание браузера. Индекс строится один раз (created()) и пересчитывается
     * при замене tableData целиком (onTableDataChange) — точечный collapse() работает
     * через прямые обращения к Map (O(1) на узел), т.е. по общей сложности O(размер
     * затронутого поддерева) вместо O(n) на каждом уровне вложенности.
     */
    private childrenIndex: Map<number, ReportDkiRRow[]> = new Map()
    private rowIndex: Map<number, ReportDkiRRow> = new Map()

    private rebuildTreeIndex() {
        const children = new Map<number, ReportDkiRRow[]>()
        const byIdx = new Map<number, ReportDkiRRow>()
        for (const row of this.tableData) {
            byIdx.set(row.idx, row)
            if (row.parent_idx !== null) {
                const siblings = children.get(row.parent_idx)
                if (siblings) {
                    siblings.push(row)
                } else {
                    children.set(row.parent_idx, [row])
                }
            }
        }
        this.childrenIndex = children
        this.rowIndex = byIdx
    }

    /** Точечное раскрытие/сворачивание одного узла дерева (клик по стрелочке строки) */
    public collapse(idx: number, collapseValue: boolean, propagate = false) {
        this.applyCollapse(idx, collapseValue, propagate)
    }

    /**
     * Здесь нельзя просто "всё в одну сторону", как в collapseAll — нужно уважать
     * состояние соседних поддеревьев. Работает через childrenIndex/rowIndex (построены
     * заранее), а не через find()/полный скан tableData на каждом уровне рекурсии.
     */
    private applyCollapse(idx: number, collapseValue: boolean, propagate: boolean) {
        const el = this.rowIndex.get(idx)
        if (!el || !el.hasChild) {
            return
        }
        const children = this.childrenIndex.get(idx)
        if (children) {
            for (const child of children) {
                child.visible = !collapseValue
                if (collapseValue || propagate) {
                    if (child.hasChild) {
                        this.applyCollapse(child.idx, collapseValue, propagate)
                    }
                    child.collapse = collapseValue
                }
            }
        }
        el.collapse = collapseValue
    }

    /**
     * Общее раскрывание/сворачивание всей таблицы (кнопка в шапке боковика, аналог CASH-EXEC).
     * Раньше вызывало collapse() для каждого корневого узла с propagate=true, что каскадно
     * пересканировало tableData целиком на каждом уровне вложенности каждого поддерева —
     * фактически O(n^2) и хуже на больших деревьях, отсюда подвисание браузера. При
     * "раскрыть/свернуть ВСЁ" итог одинаков для всех узлов сразу — не нужно уважать
     * состояние отдельных поддеревьев, поэтому один линейный проход по tableData (по
     * образцу report-svod-plan.vue::openAll), без рекурсии и без find().
     *
     * Корневые строки (parent_idx === null) намеренно не трогаем по visible — они не
     * являются "детьми" ни одного узла и приходят с бэкенда уже видимыми (RQ.eAk.RcDkiR.29);
     * трогаем только их collapse-флаг (стрелочку), если у них есть дети.
     */
    public collapseAll(collapseValue: boolean) {
        for (const item of this.tableData) {
            if (item.parent_idx !== null) {
                item.visible = !collapseValue
            }
            if (item.hasChild) {
                item.collapse = collapseValue
            }
        }
        this.tableCollapse = collapseValue
    }

    // ------------------------------------------------------------------
    // Виртуализация (RQ.eAk.RcDkiR.54)
    //
    // В проекте нет библиотеки виртуализации строк (vue-virtual-scroller и подобных),
    // добавление новой npm-зависимости в общий front — риск (Vue 2 совместимость,
    // конфликт версий, лишний вес бандла для одного отчёта). Поэтому реализовано
    // собственное окно видимости по образцу report-svod-plan.vue: реальная высота строк
    // измеряется после отрисовки (measureRenderedRows), диапазон видимых строк ищется
    // бинарным поиском по prefix-sum высот (findRowAtScrollPos) — вместо деления
    // scrollPos на фиксированный ROW_HEIGHT, что даёт неточное окно при строках разной
    // высоты. Источник позиции скролла — либо сам tableContainer (если у него есть
    // internal overflow), либо window/родительский контейнер (т.к. `.b-table-sticky-header`
    // по умолчанию не создаёт свой скролл-контейнер — overflow: visible, см.
    // src/assets/scss/root/common.scss) — оба случая учтены в syncVirtualWindow().
    // Вне диапазона строки заменяются одной строкой-прокладкой сверху и снизу нужной
    // высоты, чтобы сохранить общую высоту скролла.
    // ------------------------------------------------------------------

    public windowStart = 0
    public windowEnd = 50

    /** Реальная (измеренная) высота строки — стартует с оценки, уточняется после рендера */
    private rowHeight = INITIAL_ROW_HEIGHT
    public topSpacerHeight = 0
    public bottomSpacerHeight = 0

    private _scrollRaf: number | null = null
    private _scrollPointerTimer: any = null
    /** Отключает pointer-events на время скролла (см. <style scoped> .vs-scrolling) —
     *  предотвращает лишний recalculate hover-состояния на сотнях строк во время скролла */
    public isScrolling = false

    // _-prefixed: Vue 2 не проксирует эти поля → нереактивны (не нужен реактивный трекинг
    // на массиве измеренных высот в тысячи элементов на каждый кадр скролла).
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    private _rowHeights: number[] = []
    private _prefixSum: number[] = [0]

    public get visibleWindowRows(): ReportDkiRRow[] {
        const rows = this.filteredRows
        return rows.slice(this.windowStart, this.windowEnd)
    }

    // Построить/перестроить _rowHeights (все = текущей оценке rowHeight) и _prefixSum.
    // Вызывается при изменении длины filteredRows (загрузка данных, раскрытие/сворачивание).
    private initRowHeights(count: number) {
        const rh = this.rowHeight
        this._rowHeights = new Array(count).fill(rh)
        this.rebuildPrefixSum()
    }

    // O(n) полная пересборка prefix sum. Нереактивно — не вызывает Vue re-render.
    private rebuildPrefixSum() {
        const n = this._rowHeights.length
        const ps = new Array(n + 1)
        ps[0] = 0
        for (let i = 0; i < n; i++) {
            ps[i + 1] = ps[i] + this._rowHeights[i]
        }
        this._prefixSum = ps
    }

    // Бинарный поиск: наибольший i, для которого _prefixSum[i] <= scrollPos.
    private findRowAtScrollPos(scrollPos: number): number {
        if (this._prefixSum.length < 2) {
            return Math.floor(scrollPos / this.rowHeight)
        }
        let lo = 0
        let hi = this._prefixSum.length - 2
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            if (this._prefixSum[mid] <= scrollPos) {
                lo = mid
            } else {
                hi = mid - 1
            }
        }
        return lo
    }

    // Замер реальной высоты отрисованных строк и обновление _prefixSum при изменениях.
    // Вызывается через $nextTick после каждого обновления окна виртуализации — не вызывает re-render.
    private measureRenderedRows() {
        const container = this.$refs.tableContainer as HTMLElement | undefined
        if (!container || !this._rowHeights.length) {
            return
        }
        const rows = container.querySelectorAll<HTMLElement>('tbody tr[data-row-index]')
        let changed = false
        rows.forEach(row => {
            const idx = parseInt(row.dataset.rowIndex ?? '')
            const h = row.offsetHeight
            if (!isNaN(idx) && h > 0 && idx < this._rowHeights.length && this._rowHeights[idx] !== h) {
                this._rowHeights[idx] = h
                changed = true
            }
        })
        if (changed) {
            this.rebuildPrefixSum()
        }
    }

    /** Throttle-обёртка над scroll-событиями (и window capture-phase, и @scroll на самом контейнере) */
    private onScroll() {
        if (!this.isScrolling) {
            this.isScrolling = true
        }
        if (this._scrollPointerTimer !== null) {
            clearTimeout(this._scrollPointerTimer)
        }
        this._scrollPointerTimer = setTimeout(() => { this.isScrolling = false }, 150)

        // eqeqeq отключён намеренно: Vue 2 не проксирует _-префиксные поля на инстанс,
        // поэтому this._scrollRaf читается как undefined (не null) до первой записи.
        // undefined !== null истинно → строгое сравнение всегда бы прерывало выполнение раньше времени.
        // eslint-disable-next-line eqeqeq
        if (this._scrollRaf != null) {
            return
        }
        this._scrollRaf = requestAnimationFrame(() => {
            try {
                this.syncVirtualWindow()
            } finally {
                this._scrollRaf = null
            }
        })
    }

    /** Пересчитать окно видимости (windowStart/windowEnd) и высоты прокладок по текущей позиции скролла */
    private syncVirtualWindow() {
        const container = this.$refs.tableContainer as HTMLElement | undefined
        if (!container) {
            return
        }

        // Если у container.scrollTop > 0 — скроллится сам div (overflow-y: auto).
        // Иначе скроллится window (или родительский контейнер) — позиция берётся из bounding rect.
        let scrolledIntoContainer: number
        if (container.scrollTop > 0) {
            scrolledIntoContainer = container.scrollTop
        } else {
            const containerTop = container.getBoundingClientRect().top
            scrolledIntoContainer = Math.max(0, -containerTop)
        }

        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const total = this.filteredRows.length

        const firstVisibleRow = this.findRowAtScrollPos(scrolledIntoContainer)
        const visibleCount = Math.ceil(viewportHeight / this.rowHeight) + ROW_BUFFER * 2
        const rawStart = Math.max(0, firstVisibleRow - ROW_BUFFER)
        const rawEnd = Math.min(total, rawStart + visibleCount)

        this.windowStart = rawStart
        this.windowEnd = rawEnd

        // Высоты прокладок — из prefix sum (точные, не оценочные)
        const ps = this._prefixSum
        this.topSpacerHeight = ps.length > rawStart ? ps[rawStart] : rawStart * this.rowHeight
        this.bottomSpacerHeight = ps.length > total
            ? Math.max(0, ps[total] - ps[rawEnd])
            : Math.max(0, (total - rawEnd) * this.rowHeight)

        // Замер реальных высот строк после того, как DOM устоится — тихо обновляет _prefixSum
        this.$nextTick(() => this.measureRenderedRows())
    }

    /**
     * Пересчитать окно виртуализации при изменении видимых строк (раскрытие/сворачивание
     * узла или загрузка данных). filteredRows — computed-геттер, зависящий от `.visible`
     * каждой строки tableData; Vue отслеживает эту зависимость и вызывает watcher при
     * любой мутации `.visible` (в т.ч. из collapse()/collapseAll(), которые мутируют
     * элементы tableData "на месте", не переприсваивая массив) — по образцу
     * report-svod-plan.vue::onFilteredRowsChanged.
     */
    @Watch('filteredRows')
    private onFilteredRowsChanged() {
        this.$nextTick(() => {
            const container = this.$refs.tableContainer as HTMLElement | undefined
            if (container) {
                const firstRow = container.querySelector('tbody tr[data-row-index]') as HTMLElement | null
                if (firstRow && firstRow.offsetHeight > 0) {
                    this.rowHeight = firstRow.offsetHeight
                }
            }
            this.initRowHeights(this.filteredRows.length)
            this.syncVirtualWindow()
        })
    }

    /**
     * tableData меняется целиком (новая ссылка на массив) только при загрузке нового
     * отчёта — здесь пересчитывается индекс дерева (childrenIndex/rowIndex) и сбрасывается
     * окно виртуализации к началу. onFilteredRowsChanged (выше) сработает отдельно и
     * досчитает высоты/окно по новым данным.
     */
    @Watch('tableData')
    private onTableDataChange() {
        this.rebuildTreeIndex()
        this.windowStart = 0
        this.windowEnd = 50
    }

    // ------------------------------------------------------------------
    // Локализация (перевод с фолбэком в общее пространство reports_constructor)
    // ------------------------------------------------------------------

    public tr(key: any): string {
        if (typeof key !== 'string') {
            return ''
        }

        const tableTranslationKey = 'modules.budget_execution_monitoring.reports_constructor.dki_r.table'
        let text = this.$t(`${tableTranslationKey}.${key}`).toString()

        if (!text || text === `${tableTranslationKey}.${key}`) {
            const translationKey = 'modules.budget_execution_monitoring.reports_constructor'
            text = this.$t(`${translationKey}.${key}`).toString()

            if (!text || text === `${translationKey}.${key}`) {
                text = key
            }
        }

        if (text.includes('$date')) {
            const formattedDate = moment(this.date).format('DD.MM.YYYY')
            text = text.replaceAll('$date', formattedDate)
        }

        return text
    }

    public format(value: any): string {
        if (value == null) {
            return ''
        }
        if (Number.isNaN(Number(value))) {
            return String(value)
        }
        return formatDigitsGrouping(value)
    }
}
